<template>
  <div class="main-content container-fluid">
    <!-- Barra superior -->
    <div class="d-flex align-items-center mb-2 fb-topbar">
      <b-button
        size="sm"
        variant="outline-secondary"
        class="mr-1"
        @click="goBack"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="14"
          class="mr-50"
        />
        Voltar
      </b-button>

      <input
        v-model="formDefinition.name"
        type="text"
        placeholder="Nome do formulário"
        class="fb-title-input"
        @blur="persistDefinition"
      >

      <b-badge
        pill
        :variant="formDefinition.status ? 'success' : 'secondary'"
        class="fb-status-badge ml-2"
        role="button"
        :title="formDefinition.status ? 'Publicado — clique para voltar a rascunho' : 'Rascunho — clique para publicar'"
        @click="toggleStatus"
      >
        {{ formDefinition.status ? 'Publicado' : 'Rascunho' }}
      </b-badge>

      <div class="ml-auto">
        <b-button
          v-if="$hasAbility('forms.admin')"
          size="sm"
          variant="flat-secondary"
          class="mr-50"
          @click="showVersionHistory = true"
        >
          <feather-icon
            icon="ClockIcon"
            size="14"
            class="mr-50"
          />
          Versões
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mr-1"
          @click="showPreview = true"
        >
          <feather-icon
            icon="EyeIcon"
            size="14"
            class="mr-50"
          />
          Pré-visualizar
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          @click="confirmPublish"
        >
          <feather-icon
            icon="UploadIcon"
            size="14"
            class="mr-50"
          />
          Publicar
        </b-button>
      </div>
    </div>

    <div
      v-if="loading"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
    </div>

    <div
      v-else
      class="row fb-workspace"
    >
      <div class="col-lg-2 col-md-4 mb-2">
        <FieldPalette @add="onAddField" />
      </div>
      <div class="col-lg-6 col-md-8 mb-2">
        <FieldCanvas
          :fields="fields"
          :selected-field-id="selectedFieldId"
          @select="selectedFieldId = $event"
          @reorder="onReorder"
          @edit-logic="selectedFieldId = $event"
          @clear-branch="onClearBranch"
        />
      </div>
      <div class="col-lg-4 col-md-12 mb-2">
        <FieldProperties
          :field="selectedField"
          :all-fields="fields"
          :classifications="classifications"
          @update="onFieldUpdate"
          @delete="onFieldDelete"
        />
      </div>
    </div>

    <!-- Pré-visualização: reaproveita o componente de runtime tal como ele
         é exibido no card do envolvido, para o admin ver o resultado real.
         Quando aberta a partir do histórico de versões, previewOverrideFields
         traz o snapshot daquela versão em vez dos campos ao vivo. -->
    <b-modal
      v-model="showPreview"
      :title="previewingVersionNumber ? `Pré-visualização — Versão ${previewingVersionNumber}` : 'Pré-visualização'"
      size="lg"
      centered
      hide-footer
    >
      <FormRenderer
        v-if="showPreview"
        :form="previewForm"
        occurrence-involved-id="0"
      />
    </b-modal>

    <!-- Histórico de versões: publicar/restaurar. -->
    <b-modal
      v-model="showVersionHistory"
      title="Histórico de versões"
      size="lg"
      centered
      hide-footer
    >
      <VersionHistory
        v-if="showVersionHistory"
        ref="versionHistory"
        :form-definition-id="formDefinition.id"
        @preview="onVersionPreview"
        @restored="onVersionRestored"
      />
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BButton, BBadge, BSpinner, BModal,
} from 'bootstrap-vue'
import swal from 'sweetalert'
import FormRenderer from '@/components/involved/panels/form-renderer/FormRenderer.vue'
import FieldPalette from './FieldPalette.vue'
import FieldCanvas from './FieldCanvas.vue'
import FieldProperties from './FieldProperties.vue'
import VersionHistory from './VersionHistory.vue'
import { fieldTypeLabel } from './fieldTypes'

export default {
  name: 'FormBuilderIndex',
  components: {
    BButton,
    BBadge,
    BSpinner,
    BModal,
    FieldPalette,
    FieldCanvas,
    FieldProperties,
    FormRenderer,
    VersionHistory,
  },
  data() {
    return {
      loading: true,
      formDefinition: { name: '', description: '', status: false },
      fields: [],
      selectedFieldId: null,
      classifications: [],
      showPreview: false,
      previewOverrideFields: null,
      previewingVersionNumber: null,
      showVersionHistory: false,
    }
  },
  computed: {
    selectedField() {
      return this.fields.find(f => f.id === this.selectedFieldId) || null
    },
    previewForm() {
      return {
        id: this.formDefinition.id,
        name: this.formDefinition.name,
        description: this.formDefinition.description,
        fields: this.previewOverrideFields || this.fields,
        submission: null,
      }
    },
  },
  watch: {
    // Ao fechar a pré-visualização, descarta o override de versão antiga —
    // senão o próximo clique em "Pré-visualizar" normal mostraria por engano
    // o snapshot da última versão pré-visualizada.
    showPreview(val) {
      if (!val) {
        this.previewOverrideFields = null
        this.previewingVersionNumber = null
      }
    },
  },
  mounted() {
    this.loadAll()
  },
  methods: {
    async loadAll() {
      this.loading = true
      await Promise.all([
        this.loadFormDefinition(),
        this.loadFields(),
        this.loadClassifications(),
      ])
      this.loading = false
    },

    loadFormDefinition() {
      const { formDefinitionId } = this.$route.params
      return axios
        .get(`${this.$store.state.api}form_definitions/${formDefinitionId}`)
        .then(r => {
          this.formDefinition = r.data.data
        })
    },

    loadFields() {
      const { formDefinitionId } = this.$route.params
      return axios
        .get(`${this.$store.state.api}form_fields?form_definition_id=${formDefinitionId}`)
        .then(r => {
          this.fields = r.data.data || []
        })
    },

    loadClassifications() {
      const { nodeId, disciplineId } = this.$route.params
      return axios
        .get(`${this.$store.state.api}classifications?node_id=${nodeId}&discipline_id=${disciplineId}`)
        .then(r => {
          this.classifications = r.data.data || []
        })
        .catch(() => {})
    },

    goBack() {
      const { nodeId, disciplineId } = this.$route.params
      this.$router.push({ name: 'NodeDisciplineConfig', params: { nodeId, disciplineId } })
    },

    persistDefinition() {
      const { formDefinitionId } = this.$route.params
      axios
        .post(`${this.$store.state.api}form_definitions/${formDefinitionId}`, {
          _method: 'PUT',
          name: this.formDefinition.name,
          description: this.formDefinition.description,
          status: this.formDefinition.status,
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
    },

    toggleStatus() {
      if (this.formDefinition.status) {
        this.confirmUnpublish()
      } else {
        this.confirmPublish()
      }
    },

    // Compartilhado pelo badge de status (quando em Rascunho) e pelo botão
    // dedicado "Publicar" (sempre visível, inclusive já Publicado — republicar
    // depois de editar é uma ação válida e comum).
    confirmPublish() {
      swal({
        title: 'Publicar formulário?',
        text: 'Esta ação cria uma nova versão com o estado atual dos campos e marca o formulário como Publicado, ficando visível para os operadores. Se já existir uma versão publicada, ela será substituída como a versão vigente — mas nada é perdido: ela continua disponível no histórico de versões.',
        icon: 'warning',
        buttons: ['Cancelar', 'Publicar'],
      }).then(confirmed => {
        if (!confirmed) return
        this.$loading(true)
        axios
          .post(`${this.$store.state.api}form_definitions/${this.formDefinition.id}/publish`)
          .then(r => {
            this.formDefinition = r.data.data.form_definition
            this.$message('Publicado', 'Formulário publicado com sucesso.', 'success')
            if (this.showVersionHistory && this.$refs.versionHistory) {
              this.$refs.versionHistory.loadVersions()
            }
          })
          .catch(e => this.$message(null, e.response?.data, 'error'))
          .finally(() => this.$loading(false))
      })
    },

    confirmUnpublish() {
      swal({
        title: 'Voltar para rascunho?',
        text: 'O formulário deixará de aparecer para os operadores até ser publicado novamente.',
        buttons: ['Cancelar', 'Confirmar'],
      }).then(confirmed => {
        if (!confirmed) return
        this.formDefinition.status = false
        this.persistDefinition()
      })
    },

    onVersionPreview({ fields, versionNumber }) {
      this.previewOverrideFields = fields
      this.previewingVersionNumber = versionNumber
      this.showPreview = true
    },

    onVersionRestored({ form_definition: formDefinition }) {
      this.formDefinition = formDefinition
      this.showVersionHistory = false
      this.$message('Restaurado', 'Formulário restaurado com sucesso.', 'success')
      this.loadFields()
    },

    onAddField(fieldType) {
      const payload = {
        form_definition_id: this.formDefinition.id,
        key: `campo_${Date.now()}`,
        label: `${fieldTypeLabel(fieldType)} sem título`,
        field_type: fieldType,
        required: false,
      }
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}form_fields`, payload)
        .then(r => {
          const created = r.data
          this.fields.push(created)
          this.selectedFieldId = created.id
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    // Monta o payload "completo" exigido pela validação do backend (vários
    // campos são `required` tanto na criação quanto na atualização), a
    // partir do estado atual do campo mais os overrides desejados — usado
    // pelos atalhos de lógica do canvas (remover um branch específico).
    buildFieldPayload(field, overrides = {}) {
      return {
        form_definition_id: this.formDefinition.id,
        key: field.key,
        label: field.label,
        field_type: field.field_type,
        required: !!field.required,
        help_text: field.help_text || null,
        order: field.order,
        is_root: !!field.is_root,
        next_field_id_if_yes: field.next_field_id_if_yes || null,
        next_field_id_if_no: field.next_field_id_if_no || null,
        classification_id: field.classification_id || null,
        status: field.status,
        ...overrides,
      }
    },

    patchField(field, overrides) {
      const payload = this.buildFieldPayload(field, overrides)
      this.$loading(true)
      return axios
        .post(`${this.$store.state.api}form_fields/${field.id}`, { ...payload, _method: 'PUT' })
        .then(() => this.loadFields())
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    onReorder(items) {
      axios
        .post(`${this.$store.state.api}form_fields/reorder`, {
          form_definition_id: this.formDefinition.id,
          items,
        })
        .then(() => this.loadFields())
        .catch(() => this.$message('Erro', 'Falha ao salvar a nova ordem.', 'error'))
    },

    onClearBranch({ fieldId, branch }) {
      const field = this.fields.find(f => f.id === fieldId)
      if (!field) return
      if (branch === 'yes') {
        this.patchField(field, { next_field_id_if_yes: null })
      } else if (branch === 'no') {
        this.patchField(field, { next_field_id_if_no: null })
      } else {
        this.clearOptionBranch(field, branch)
      }
    },

    clearOptionBranch(field, optionId) {
      const opt = (field.options || []).find(o => o.id === optionId)
      if (!opt) return
      this.$loading(true)
      axios
        .post(`${this.$store.state.api}form_field_options/${optionId}`, {
          _method: 'PUT',
          form_field_id: field.id,
          label: opt.label,
          value: opt.value,
          order: opt.order,
          next_field_id: null,
          classification_id: opt.classification_id || null,
        })
        .then(() => this.loadFields())
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    // Sincroniza as opções (single/multiple_choice e image_annotation): sem
    // endpoint em lote, então removidas/alteradas/novas são resolvidas uma a
    // uma — mesmo padrão já usado na antiga Seção B de DisciplineConfig.vue.
    syncFieldOptions(fieldId, options, removedOptionIds) {
      const calls = []
      removedOptionIds.forEach(id => {
        calls.push(axios.delete(`${this.$store.state.api}form_field_options/${id}`))
      })
      options.forEach((opt, idx) => {
        const optPayload = {
          form_field_id: fieldId,
          label: opt.label,
          value: opt.value,
          order: idx,
          next_field_id: opt.next_field_id ?? null,
          classification_id: opt.classification_id ?? null,
        }
        if (opt.id) {
          calls.push(axios.post(`${this.$store.state.api}form_field_options/${opt.id}`, { ...optPayload, _method: 'PUT' }))
        } else {
          calls.push(axios.post(`${this.$store.state.api}form_field_options`, optPayload))
        }
      })
      return Promise.all(calls)
    },

    onFieldUpdate({
      id, data, options, removedOptionIds, imageFile,
    }) {
      let body = { ...data, _method: 'PUT' }
      if (imageFile) {
        const formData = new FormData()
        Object.keys(data).forEach(k => {
          if (data[k] !== null && data[k] !== undefined) {
            let value = data[k]
            if (typeof value === 'boolean') value = value ? 1 : 0
            formData.append(k, value)
          }
        })
        formData.append('base_image', imageFile)
        formData.append('_method', 'PUT')
        body = formData
      }

      this.$loading(true)
      axios
        .post(`${this.$store.state.api}form_fields/${id}`, body)
        .then(() => this.syncFieldOptions(id, options || [], removedOptionIds || []))
        .then(() => this.loadFields())
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },

    onFieldDelete(fieldId) {
      this.$loading(true)
      axios
        .delete(`${this.$store.state.api}form_fields/${fieldId}`)
        .then(() => {
          this.$message('Sucesso', 'Campo excluído.', 'success')
          if (this.selectedFieldId === fieldId) this.selectedFieldId = null
          return this.loadFields()
        })
        .catch(e => this.$message(null, e.response?.data, 'error'))
        .finally(() => this.$loading(false))
    },
  },
}
</script>

<style scoped>
.fb-title-input {
  border: none;
  background: transparent;
  font-size: 1.1rem;
  font-weight: 600;
  max-width: 360px;
  padding: 2px 6px;
}
.fb-title-input:focus {
  outline: none;
  border-bottom: 1px solid #7367f0;
}
.fb-status-badge {
  cursor: pointer;
}
.fb-workspace {
  min-height: 60vh;
}

@media (max-width: 767.98px) {
  .fb-topbar {
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .fb-title-input {
    max-width: 100%;
    width: 100%;
  }
}
</style>
